/**
 * planPrices.ts — attach LIVE per-plan prices to the plan-preference question.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY: the q11 plan-preference question (the weekly-dose vs daily-pill choice)
 * used to carry a baked `from £—/month [PLACEHOLDER — CTO feed]` line in its
 * server helpText. Prices are owned by prices-service, not
 * the question config, so the CTO decision (2026-08-06) is to render the REAL
 * "from £X/month" against each option from the LIVE prices feed instead.
 *
 * The price source is the SAME authed, post-eligibility read the plan-selection
 * screen already uses (`GET /v1/prices/prices/options` → getPlanOptions). It is
 * NOT a public surface: the read requires a session, so during the guest
 * (pre-account) questionnaire we deliberately do NOT fetch — the question shows
 * no price at all (a graceful fallback, never a fake number), and the real
 * price lands at plan selection after the account exists. Where the portal IS
 * authenticated (the staff-assisted form), the live price renders in place.
 * R28: q11 is served only after the eligibility gate, so a med/price is
 * permitted here — and because we never expose it on a public route, nothing
 * leaks pre-eligibility.
 *
 * R28 SOURCE-SCAN NOTE: `r28.spec.tsx` greps portal source for the restricted
 * delivery-form word (the injectable one) and brand names, so this file must
 * never spell them. It doesn't need to: the join between an option and its
 * price is done on DATA at runtime — an option key embeds its delivery-form
 * token (`daily_pill` → form `pill`) and we match it against the runtime
 * `PlanOption.form` value, which is never written as a literal in source.
 */
import { useEffect, useState } from 'react';
import { getPlanOptions } from '../api/prices';
import { getToken } from '../auth/session';
import type { PlanOption, QuestionPublic } from '../api/types';

/**
 * The plan-preference question is the ONE single_select that offers a
 * "Help me choose" escape hatch (the clinician-recommends option). That marker
 * is unique to q11 across the whole seed — the prior-treatment question (q10)
 * has med options but no help-me-choose — so it identifies the question without
 * hard-coding a question key or spelling a restricted delivery-form word.
 */
const HELP_ME_CHOOSE = /help[_\s-]?me[_\s-]?choose/i;

export function isPlanPreferenceQuestion(question: QuestionPublic): boolean {
  if (question.kind !== 'single_select') return false;
  const options = question.options ?? [];
  return options.length >= 2 && options.some((o) => HELP_ME_CHOOSE.test(o.key));
}

/**
 * The cheapest live monthly price (integer pence) for the delivery form an
 * option represents, or null when nothing priced matches. An option key embeds
 * its delivery-form token (`daily_pill`, `weekly_<form>`), so we join to the
 * priced catalogue by testing whether the key CONTAINS a `PlanOption.form`
 * value — form strings come from the server, never a literal here. The
 * "Help me choose" option matches no form, so it correctly gets no price.
 *
 * 0-pence rows are the launch placeholder ("price not set yet") — treated as
 * unpriced so a plan never shows "£0", exactly the fake-number the fallback
 * exists to avoid.
 */
export function cheapestPenceForOptionKey(optionKey: string, options: PlanOption[]): number | null {
  const key = optionKey.toLowerCase();
  let cheapest: number | null = null;
  for (const option of options) {
    if (!option.form || !key.includes(option.form.toLowerCase())) continue;
    for (const plan of option.plans) {
      if (plan.monthlyPence > 0 && (cheapest === null || plan.monthlyPence < cheapest)) {
        cheapest = plan.monthlyPence;
      }
    }
  }
  return cheapest;
}

/**
 * Defensively drop any price/placeholder sentence from a stored helpText.
 *
 * The seed default no longer carries a price line, but the boot seeder is
 * PRESENCE-idempotent — an environment seeded before this change still holds
 * the old `... from £—/month ... [PLACEHOLDER — CTO feed]. Not sure? ...` copy
 * in the DB and will never be re-seeded. So we strip, at render time, any
 * sentence carrying a `£`, a `/month`, or a `[PLACEHOLDER …]` marker and keep
 * the neutral remainder — the placeholder can never reach a patient regardless
 * of when their tenant was seeded. A helpText with no such sentence is
 * returned unchanged.
 */
export function stripPriceCopy(text: string | undefined): string | undefined {
  if (!text) return text;
  const cleaned = text
    .split(/(?<=\.)\s+/)
    .filter((sentence) => !/£|\/month|\[placeholder/i.test(sentence))
    .join(' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return cleaned.length > 0 ? cleaned : undefined;
}

/**
 * Live plan options for the plan-preference question, or null when unavailable
 * (guest/no session, disabled, prices unconfigured, or the read failed) — the
 * caller renders no price in every null case. `enabled` is the "this is the
 * plan-preference question" gate so we never fetch on the other questions.
 */
export function usePlanFormPrices(clinicSlug: string, enabled: boolean): PlanOption[] | null {
  const [options, setOptions] = useState<PlanOption[] | null>(null);

  useEffect(() => {
    // The /options read is authed (post-eligibility). No session → never fire a
    // guaranteed-401 fetch; the question falls back to no price (see file doc).
    if (!enabled || !getToken()) {
      setOptions(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await getPlanOptions(clinicSlug);
        if (!cancelled) setOptions(response.options);
      } catch {
        // Price preview is an enhancement, never a blocker — swallow and fall back.
        if (!cancelled) setOptions(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clinicSlug, enabled]);

  return options;
}
